import { Signal, WritableSignal } from '@angular/core';
import { Observable } from 'rxjs';
import { UserModel } from '@models/resources/user.model';

/**
 * Payload für die Self-Registration (V4, Sprint 5b ADR-005 D2).
 * Spiegelt das `RegisterRequest`-DTO im Backend 1:1.
 *
 * Anschrift seit Sprint 5c Post-Sprint-Fix Bestandteil des Self-Reg-
 * Pfades — alle vier Felder sind Pflicht. Das FE pre-fillt
 * `address_country` mit `AT`, das BE setzt keinen Default.
 */
export interface RegisterPayload {
  email: string;
  password: string;
  password_confirmation: string;
  first_name: string;
  last_name: string;
  phone: string;
  address_street: string;
  address_zip: string;
  address_city: string;
  address_country: string;
}

/**
 * Activation-Body. `password` ist nur bei der Admin-Stub-Anlage required
 * (Backend prüft, ob `users.password === NULL` ist und verlangt dann das
 * Passwort hier; bei Self-Reg-Pfad ignoriert das Backend das Feld).
 */
export interface ActivatePayload {
  email: string;
  token: string;
  password?: string;
  password_confirmation?: string;
}

/**
 * Abstract contract for authentication services (V4, Sprint 5c).
 *
 * Zwei konkrete Implementierungen:
 * - `DummyAuthService` für Demo-Mode (Sprint 1–4 FE-First-Stack), liest
 *   eine optionale `?role=` aus der URL, akzeptiert beliebige Credentials,
 *   gibt sofort einen Pseudo-Sanctum-Token zurück. Kein Backend nötig.
 * - `HttpAuthService` fährt gegen das Laravel-Backend V4 (Sprint 5b):
 *   `POST /api/login`, `POST /api/auth/register`, `POST /api/auth/activate`,
 *   `POST /api/auth/activate/resend`, `POST /api/auth/password/forgot`,
 *   `POST /api/auth/password/reset`, `POST /api/logout`, `GET /api/me`.
 *
 * Die aktive Variante wird in `app.config.ts` über `environment.authMode`
 * (`'dummy' | 'http'`) gebunden. Consumer injizieren `AuthService` und
 * merken nichts von der Umstellung.
 *
 * **V4-Cleanup (Sprint 5c S5c-4):** 2FA-Schicht (`verify2fa`, `resend2fa`,
 * `pendingLogin`-Signal) komplett entfernt. Login ist einstufig, Token
 * kommt direkt aus `login()`. Self-Reg + Activate sind neu (ADR-005).
 */
export abstract class AuthService {
  abstract readonly user: WritableSignal<UserModel | null>;
  abstract readonly isAuthenticated: Signal<boolean>;

  /**
   * V4-Login: Credentials prüfen, Sanctum-Token + User direkt zurückgeben.
   * Bei 401: Error mit generischer Message (kein Account-Enumeration).
   * Setzt `user` bei Erfolg.
   */
  abstract login(email: string, password: string): Observable<UserModel>;

  /**
   * V4-Self-Registration. Backend antwortet 202 + verschickt Activation-
   * Mail mit 24h-Token-Link. Customer-Konto ist noch nicht login-fähig.
   */
  abstract register(payload: RegisterPayload): Observable<void>;

  /**
   * V4-Account-Activation (Defense-in-depth-Pivot 2026-05-13): Token
   * aus dem Mail-Link einlösen, Backend setzt `is_active=true` und
   * vergibt `depot_number`. Gibt **keinen** Sanctum-Token zurück —
   * Customer muss sich danach explizit auf `/auth/login` einloggen.
   * Bei Admin-Stub-Anlage muss `password` im Payload sein.
   */
  abstract activate(payload: ActivatePayload): Observable<void>;

  /**
   * Neuen Activation-Link anfordern (z.B. nach Ablauf des ursprünglichen
   * 24h-Tokens). Antwort ist immer 202, kein User-Leak.
   */
  abstract resendActivation(email: string): Observable<void>;

  /** Passwort-Reset-Link anfordern. Antwort ist generisch (kein User-Leak). */
  abstract requestPasswordReset(email: string): Observable<void>;

  /**
   * Passwort mit Reset-Token neu setzen.
   *
   * Sprint 7 S7-20: BE braucht zusätzlich die E-Mail-Adresse (Standard-
   * Laravel-Password-Broker-Convention) — sie kommt aus dem Query-Param
   * des Reset-Mail-Links (`/auth/reset?token=…&email=…`).
   */
  abstract resetPassword(token: string, password: string, email: string): Observable<void>;

  /** Logout – invalidiert Token und räumt lokalen State auf. */
  abstract logout(): Observable<void>;

  /** Rehydriert eine sessionStorage-Session beim App-Start. */
  abstract hydrate(): Observable<UserModel | null>;

  /** Lokalen State hart leeren. */
  abstract clear(): void;
}
